import { Link } from "react-router-dom";
import BookButton from "@/components/BookButton";
import { useSEO, breadcrumbLd } from "@/lib/seo";
import { PROPERTY } from "@/lib/constants";

const PILLARS = [
  {
    title: "Quality you can inspect",
    body: "Renovated rooms, LED-lit baths, crisp linens turned over to the same standard every single day. We don't promise luxury — we promise that everything in your room works, is clean, and was checked before you arrived.",
  },
  {
    title: "Service from real people",
    body: "A small team, a 24-hour front desk, and a phone that gets answered. If something isn't right, you tell a human who can fix it — tonight, not after a support ticket.",
  },
  {
    title: "Built around your needs",
    body: "We're not a chain following a manual. We listen to who walks through the door — families, crews, truckers, hunters, wedding guests — and we shape the stay around them with the resources we have.",
  },
  {
    title: "Gratitude, every stay",
    body: "You could have driven past. You didn't. Every guest who chooses Westbridge becomes part of this place's story, and we don't take that lightly. Thank you for making us part of your journey — and your life.",
  },
];

export default function About() {
  useSEO({
    title: "Our Story — What Westbridge Stands For",
    description:
      "Westbridge Inn & Suites is an independently owned hotel in Carrollton, Missouri built on quality, service and genuine gratitude. Learn what the Westbridge name means and the promise behind it.",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "About Westbridge Inn & Suites",
        url: "https://www.westbridgecarrollton.com/about",
        mainEntity: {
          "@type": "Hotel",
          name: "Westbridge Inn & Suites",
          slogan: "Great service. Clean rooms. The bridge between the road and a good night's rest.",
          foundingLocation: "Carrollton, Missouri",
          knowsAbout: ["hospitality", "Carroll County Missouri", "US Highway 65 travel"],
        },
      },
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Our Story", path: "/about" }]),
    ],
  });

  return (
    <div data-testid="about-page">
      <section className="pt-36 pb-16 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">Our story</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              A bridge between the road<br /><span className="italic text-ember">and a good night's rest.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              Westbridge Inn &amp; Suites is an independently owned, 34-room hotel on US Highway 65 in
              Carrollton, Missouri — and the Westbridge name is a promise we make out loud.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-sand py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <img
              src="/images/property/exterior-main.jpg"
              alt="Westbridge Inn & Suites building on US Highway 65 in Carrollton, Missouri"
              className="w-full aspect-[4/3] object-cover"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-6 space-y-6 text-ink-soft text-lg leading-relaxed">
            <h2 className="font-display text-4xl md:text-5xl text-ink leading-tight">What the name means</h2>
            <p>
              A bridge carries you across the long middle of a journey. Out here on US-65 — halfway
              between everywhere and everywhere else — that's exactly what this hotel is: the bridge
              between the miles behind you and the rest you've earned. The gradient-orange W on our
              sign reads from half a mile out, and underneath it sits the whole brand in six words:
              <strong className="text-ink"> great service, clean rooms, fair prices.</strong>
            </p>
            <p data-testid="about-gratitude">
              We measure success one way: whether you'd stop again. Every guest who chooses Westbridge
              becomes part of our story, and we're genuinely thankful for it — thank you for having us
              be part of your trip, your work season, your family reunion, your life.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="font-mono-label text-ember mb-4">The Westbridge promise</p>
          <h2 className="font-display text-5xl md:text-6xl text-ink leading-tight mb-14">
            Four things we<br /><span className="italic text-ember">stand for.</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-x-14 gap-y-12">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="border-t border-rule-subtle pt-6" data-testid={`about-pillar-${i}`}>
                <p className="font-mono-label text-ember mb-3">0{i + 1}</p>
                <h3 className="font-display text-3xl text-ink mb-3">{p.title}</h3>
                <p className="text-ink-soft leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-4xl md:text-5xl text-canvas leading-tight">
            Thank you for staying with us.
          </h2>
          <p className="mt-5 text-canvas/75 text-lg leading-relaxed">
            From the whole Westbridge team in Carrollton — we'll keep the lights on, the breakfast
            hot, and your room exactly the way you hope it will be.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <BookButton label="Book Direct & Save" testid="about-book-btn" />
            <a href={`tel:${PROPERTY.phone.replace(/[^\d+]/g, "")}`} className="btn-outline-canvas font-mono-label px-6 py-3 border border-canvas/40 text-canvas hover:bg-canvas hover:text-ink transition-colors" data-testid="about-call-btn">
              Call {PROPERTY.phone}
            </a>
          </div>
          <p className="mt-8">
            <Link to="/accommodations" className="link-underline font-mono-label text-canvas/70">
              See our six room types →
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
